/**
 * @fileoverview Shared theme swatch grid component for selecting a UI theme.
 * @module ui-admin/components/profile/ThemeSwatchGrid
 *
 * Responsibilities:
 * - Render a grid of colored swatches, one per available theme.
 * - Highlight the currently selected theme.
 * - Optionally badge the tenant default theme.
 * - Call onSelectTheme when the user picks a theme.
 *
 * Dependencies:
 * - GridTheme type from ui-admin ThemeContext
 *
 * Architecture:
 * - Pure presentation component with no Dexie, API, or context access.
 * - Designed to be used inside UserProfileDropdown and TenantDefaultThemeSection.
 * - Uses inline styles for portability across both portals.
 *
 * Notes:
 * - The swatch circle uses the theme's accent color as the fill.
 * - The swatch background preview uses the theme's headerBg color as a ring.
 * - Themes are laid out in a responsive 4-column grid.
 */

import React from 'react';
import { Check } from 'lucide-react';
import type { GridTheme } from '../../contexts/ThemeContext';

/**
 * @contract ThemeSwatchGridProps
 *
 * Purpose:
 * - Props for the ThemeSwatchGrid component.
 *
 * Fields:
 * - themes: Full list of available themes to render.
 * - selectedThemeId: Id of the currently active theme (highlighted with checkmark).
 * - tenantDefaultThemeId: Optional id of the department-wide default (shown with badge).
 * - onSelectTheme: Called with the theme id when the user clicks a swatch.
 *
 * Rules:
 * - selectedThemeId must match a theme id in the themes array for highlighting to work.
 * - tenantDefaultThemeId may be null/undefined; badge is omitted when absent.
 *
 * Sync:
 * - Pure presentation; no side effects.
 */
export interface ThemeSwatchGridProps {
  themes: GridTheme[];
  selectedThemeId: string;
  tenantDefaultThemeId?: string | null;
  onSelectTheme: (themeId: string) => void;
}

/**
 * @component ThemeSwatchGrid
 *
 * Purpose:
 * - Display all themes as clickable accent-colored swatches with selection indicator.
 *
 * Props:
 * - themes: Array of GridTheme objects.
 * - selectedThemeId: Currently active theme id.
 * - tenantDefaultThemeId: Optional tenant default theme id (shows a small badge).
 * - onSelectTheme: Callback invoked with the chosen theme id.
 *
 * Behavior:
 * - Renders a grid of swatches; the selected one shows a white check icon.
 * - The tenant default swatch shows a small "Dept" label beneath it.
 * - Keyboard accessible: each swatch is a <button> element.
 *
 * Data Source:
 * - local (props only)
 *
 * Constraints:
 * - No API calls. No context reads. Pure props-driven rendering.
 *
 * Performance:
 * - No memoization needed; renders synchronously from props.
 *
 * @reusable
 *
 * Usage:
 * - UserProfileDropdown (both portals)
 * - TenantDefaultThemeSection (tenant-web settings page)
 *
 * Rules:
 * - MUST be presentation-only; no stateful logic.
 */
export function ThemeSwatchGrid({ themes, selectedThemeId, tenantDefaultThemeId, onSelectTheme }: ThemeSwatchGridProps) {
  return (
    <div
      className="grid gap-2 p-2"
      style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      role="listbox"
      aria-label="Select a theme"
    >
      {themes.map((t) => {
        const isSelected = t.id === selectedThemeId;
        const isDefault = t.id === tenantDefaultThemeId;
        return (
          <div key={t.id} className="flex flex-col items-center gap-0.5">
            <button
              role="option"
              aria-selected={isSelected}
              aria-label={`${t.name}${isDefault ? ' (department default)' : ''}`}
              onClick={() => onSelectTheme(t.id)}
              className="relative flex items-center justify-center rounded-full transition-transform hover:scale-110 focus:outline-hidden focus-visible:ring-2"
              style={{
                width: 28,
                height: 28,
                background: t.accent,
                boxShadow: isSelected
                  ? `0 0 0 2px #fff, 0 0 0 4px ${t.accent}`
                  : `0 0 0 2px ${isDefault ? t.accent : '#d1d5db'}`,
              }}
              title={t.name}
            >
              {isSelected && <Check size={14} color="#fff" strokeWidth={3} />}
            </button>
            {isDefault && (
              <span
                className="text-center leading-tight"
                style={{ fontSize: 9, color: '#6b7280', whiteSpace: 'nowrap' }}
              >
                Dept
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
